import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import RuleListComponent from "./component";

const RuleListContainer = (props) => {
  const { corporateId, departmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetRules, setResetRules] = useState([]);
  const [carryForwardRules, setCarryForwardRules] = useState([]);
  const [corporateName, setCorporateName] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [ruleStatus, setRuleStatus] = useState("active");
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [deleteRuleType, setDeleteRuleType] = useState(null);
  const [deleteDepartmentId, setDeleteDepartmentId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Get corporate name from query params
  const corporateNameFromQuery = queryParams.get('corporateName') || "";

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1);
  };

  // Handle add rule
  const handleAddRule = () => {
    navigate(`/home/customermanager/corporate/add-rule/${corporateId}`);
  };

  // Handle edit rule
  const handleEditRule = (departmentId, departmentName, ruleType) => {
    console.log("Editing rule - Department:", departmentId, "Type:", ruleType);
    
    navigate(`/home/customermanager/corporate/edit-rule/${corporateId}/${departmentId}`, {
      state: {
        departmentName: departmentName,
        ruleType: ruleType
      }
    });
  };

  // Handle delete rule click
  const handleDeleteRule = (ruleId, ruleType, departmentId) => {
    if (departmentId === undefined || departmentId === null) {
      toastr.error("Error", "Invalid department ID");
      return;
    }
    
    setRuleToDelete(ruleId);
    setDeleteRuleType(ruleType);
    setDeleteDepartmentId(departmentId);
    setShowDeleteModal(true);
  };

  // Handle status filter change
  const handleStatusChange = (status) => {
    setRuleStatus(status);
  };

  // Handle toggle rule status
  const handleToggleRuleStatus = async (ruleId, ruleType, currentStatus) => {
    try {
      const newStatusText = currentStatus ? 'inactive' : 'active';
      
      const endpoint = `/partner/api/v1/corporate/${corporateId}/recharge-rules/${ruleId}/status`;
      const payload = { isActive: !currentStatus };
      
      await props.getDataFromAPI(
        endpoint,
        "PATCH",
        payload,
        (response) => {
          if (ruleType === 'reset') {
            setResetRules(prev => prev.map(rule => 
              rule.ruleId === ruleId 
                ? { ...rule, isActive: !currentStatus }
                : rule
            ));
          } else {
            setCarryForwardRules(prev => prev.map(rule => 
              rule.ruleId === ruleId 
                ? { ...rule, isActive: !currentStatus }
                : rule
            ));
          }
          
          toastr.success("Success", `Rule status updated to ${newStatusText}`);
        },
        (err) => {
          console.error("Error updating rule status:", err);
          toastr.error("Error", "Failed to update rule status");
        }
      );
      
    } catch (err) {
      console.error("Error updating rule status:", err);
      toastr.error("Error", "Failed to update rule status");
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setRuleToDelete(null);
    setDeleteRuleType(null);
    setDeleteDepartmentId(null);
    setIsDeleting(false);
  };

  // Handle confirm delete - CORRECTED with refresh after delete
  const handleConfirmDelete = async () => {
    // Validate rule to delete
    if (!ruleToDelete) {
      toastr.error("Error", "Rule ID is missing");
      return;
    }
    
    // Validate delete rule type
    if (!deleteRuleType) {
      toastr.error("Error", "Rule type is missing");
      return;
    }
    
    // Validate corporate ID
    if (!corporateId) {
      toastr.error("Error", "Corporate ID is missing");
      return;
    }
    
    // Validate department ID - Allow 0 as valid ID
    if (deleteDepartmentId === undefined || deleteDepartmentId === null) {
      toastr.error("Error", "Department ID is missing");
      return;
    }

    try {
      setIsDeleting(true);
      
      // Use the correct API endpoint structure
      const deleteUrl = `/partner/api/v1/recharge-rules/${corporateId}/${deleteDepartmentId}`;
      
      // Prepare payload with ruleId to identify which rule to delete
      const payload = {
        ruleId: ruleToDelete,
        ruleType: deleteRuleType
      };
      
      console.log("Deleting rule with URL:", deleteUrl);
      console.log("Payload:", payload);
      
      await props.getDataFromAPI(
        deleteUrl,
        "DELETE",
        payload,
        (response) => {
          console.log("Delete successful, refreshing rule list...");
          
          // Close modal first
          setShowDeleteModal(false);
          setRuleToDelete(null);
          setDeleteRuleType(null);
          setDeleteDepartmentId(null);
          setIsDeleting(false);
          
          // Show success message
          toastr.success("Success", `${deleteRuleType === 'reset' ? 'Reset' : 'Carry Forward'} rule deleted successfully`);
          
          // IMPORTANT: Refresh the rules list after successful deletion
          fetchRules();
        },
        (err) => {
          console.error("Error deleting rule:", err);
          
          // Try alternative approach - send ruleId as query parameter
          const alternativeUrl = `/partner/api/v1/recharge-rules/${corporateId}/${deleteDepartmentId}?ruleId=${ruleToDelete}&ruleType=${deleteRuleType}`;
          
          console.log("Trying alternative URL:", alternativeUrl);
          
          props.getDataFromAPI(
            alternativeUrl,
            "DELETE",
            undefined,
            (altResponse) => {
              console.log("Alternative delete successful, refreshing rule list...");
              
              setShowDeleteModal(false);
              setRuleToDelete(null);
              setDeleteRuleType(null);
              setDeleteDepartmentId(null);
              setIsDeleting(false);
              
              toastr.success("Success", `${deleteRuleType === 'reset' ? 'Reset' : 'Carry Forward'} rule deleted successfully`);
              
              // IMPORTANT: Refresh the rules list after successful deletion
              fetchRules();
            },
            (altErr) => {
              console.error("Alternative delete also failed:", altErr);
              toastr.error("Error", altErr?.message || "Failed to delete rule. Please try again.");
              setShowDeleteModal(false);
              setIsDeleting(false);
            }
          );
        }
      );
      
    } catch (err) {
      console.error("Error in delete operation:", err);
      toastr.error("Error", "Failed to delete rule");
      setShowDeleteModal(false);
      setIsDeleting(false);
    }
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const day = parseInt(parts[2]);
          const newDate = new Date(year, month, day);
          if (!isNaN(newDate.getTime())) {
            return formatDateObject(newDate);
          }
        }
        return dateString;
      }
      return formatDateObject(date);
    } catch (err) {
      console.error("Error formatting date:", err, dateString);
      return dateString;
    }
  };

  const formatDateObject = (date) => {
    const day = date.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : 
                   day % 10 === 2 && day !== 12 ? 'nd' : 
                   day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day}${suffix} ${month} ${year}`;
  };

  // Format frequency to readable format
  const formatFrequency = (freq) => {
    if (!freq) return 'Monthly';
    const freqMap = {
      'MONTHLY': 'Monthly',
      'WEEKLY': 'Weekly',
      'DAILY': 'Daily',
      'YEARLY': 'Yearly',
      'Monthly': 'Monthly',
      'Weekly': 'Weekly',
      'Daily': 'Daily',
      'Yearly': 'Yearly'
    };
    return freqMap[freq] || freq.charAt(0).toUpperCase() + freq.slice(1).toLowerCase();
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹ 0.00';
    return `₹ ${parseFloat(amount).toFixed(2)}`;
  };

  // Fetch rules from API
  const fetchRules = useCallback(() => {
    if (!corporateId) {
      setError("Corporate ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let endpoint = `/partner/api/v1/corporate/${corporateId}/recharge-rules`;
    if (departmentId !== undefined && departmentId !== null && departmentId !== 0) {
      endpoint += `?departmentId=${departmentId}`;
    }

    console.log("Fetching rules from:", endpoint);

    props.getDataFromAPI(
      endpoint,
      "GET",
      undefined,
      (response) => {
        try {
          console.log("API Response after delete:", response);
          
          let resetData = [];
          let carryForwardData = [];
          
          if (response && response) {
            const { resetRules = [], carryForwardRules = [] } = response;
            
            if (Array.isArray(resetRules)) {
              resetData = resetRules.map(rule => ({
                ruleId: rule.ruleId,
                departmentId: rule.departmentId,
                department: rule.department || "General",
                amount: formatCurrency(rule.amount),
                frequency: formatFrequency(rule.frequency),
                resetOn: formatDate(rule.resetOn),
                isActive: rule.isActive !== undefined ? rule.isActive : true
              }));
            }
            
            if (Array.isArray(carryForwardRules)) {
              carryForwardData = carryForwardRules.map(rule => ({
                ruleId: rule.ruleId,
                departmentId: rule.departmentId,
                department: rule.department || "General",
                amount: formatCurrency(rule.amount),
                minRequest: formatCurrency(rule.minRequest),
                frequency: formatFrequency(rule.frequency),
                carryForwardOn: formatDate(rule.carryForwardOn),
                isActive: rule.isActive !== undefined ? rule.isActive : true
              }));
            }
          }
          
          setResetRules(resetData);
          setCarryForwardRules(carryForwardData);
          
          if (corporateNameFromQuery) {
            setCorporateName(corporateNameFromQuery);
          } else {
            fetchCorporateDetails();
          }
          
        } catch (err) {
          console.error("Error processing rule data:", err);
          setError("Failed to process rule data");
        }
        
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching rules:", err);
        setError("Failed to load rules. Please try again.");
        setLoading(false);
        toastr.error("Failed", "Unable to fetch rules");
      }
    );
  }, [corporateId, departmentId, corporateNameFromQuery, props.getDataFromAPI]);

  // Fetch corporate details separately if needed
  const fetchCorporateDetails = useCallback(() => {
    if (!corporateNameFromQuery && corporateId) {
      props.getDataFromAPI(
        `/partner/api/v2/corporate/${corporateId}`,
        "GET",
        undefined,
        (response) => {
          if (response && (response.corporateName || response.name)) {
            setCorporateName(response.corporateName || response.name);
          } else {
            setCorporateName(`Corporate ${corporateId}`);
          }
        },
        (err) => {
          console.error("Error fetching corporate details:", err);
          setCorporateName(`Corporate ${corporateId}`);
        }
      );
    } else if (corporateNameFromQuery) {
      setCorporateName(corporateNameFromQuery);
    }
  }, [corporateId, corporateNameFromQuery, props.getDataFromAPI]);

  // Initial data fetch
  useEffect(() => {
    if (corporateId) {
      fetchRules();
      if (!corporateNameFromQuery) {
        fetchCorporateDetails();
      }
    }
  }, [corporateId, fetchRules, fetchCorporateDetails, corporateNameFromQuery]);

  // Refresh rules when needed
  const refreshRules = () => {
    fetchRules();
  };

  return (
    <RuleListComponent
      loading={loading}
      error={error}
      resetRules={resetRules}
      carryForwardRules={carryForwardRules}
      corporateName={corporateName}
      departmentName={departmentName}
      ruleStatus={ruleStatus}
      showDeleteModal={showDeleteModal}
      ruleToDelete={ruleToDelete}
      deleteRuleType={deleteRuleType}
      deleteDepartmentId={deleteDepartmentId}
      isDeleting={isDeleting}
      onBackClick={handleBackClick}
      onAddRuleClick={handleAddRule}
      onEditRuleClick={handleEditRule}
      onDeleteRuleClick={handleDeleteRule}
      onStatusChange={handleStatusChange}
      onToggleRuleStatus={handleToggleRuleStatus}
      onCancelDelete={handleCancelDelete}
      onConfirmDelete={handleConfirmDelete}
      onRefreshRules={refreshRules}
    />
  );
};

const mapStateToProps = (state) => {
  return {};
};

export default connect(mapStateToProps, {
  getDataFromAPI,
})(RuleListContainer);